import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FocusModeContext = createContext(null);

// Path type configurations with display names and colors
export const PATH_TYPES = {
  college: {
    id: 'college',
    name: 'College Bound',
    description: 'Focus on college admissions, SAT/ACT prep, and scholarships',
    color: 'blue',
    icon: '🎓',
    gradient: 'from-blue-600 to-cyan-600',
    careerCategories: ['tech', 'healthcare', 'business', 'professional', 'creative'],
    suggestedCareers: ['Software Engineering', 'Healthcare', 'Business & Finance', 'Data Science', 'Law', 'Medicine']
  },
  trade: {
    id: 'trade',
    name: 'Skilled Trades',
    description: 'Focus on apprenticeships, certifications, and hands-on careers',
    color: 'amber',
    icon: '🔧',
    gradient: 'from-amber-600 to-orange-600',
    careerCategories: ['trade', 'transportation'],
    suggestedCareers: ['Electrician', 'Plumber', 'HVAC', 'Welding', 'Carpentry', 'Diesel Mechanic', 'CNC Machinist']
  },
  military: {
    id: 'military',
    name: 'Military Service',
    description: 'Focus on military branches, ASVAB prep, and enlistment',
    color: 'emerald',
    icon: '⭐',
    gradient: 'from-emerald-600 to-green-600',
    careerCategories: ['military', 'tech', 'healthcare', 'transportation'],
    suggestedCareers: ['Military Officer', 'Military Engineering', 'Military Healthcare', 'Aviation', 'Cybersecurity']
  },
  entrepreneurship: {
    id: 'entrepreneurship',
    name: 'Entrepreneurship',
    description: 'Focus on starting your own business and building skills',
    color: 'purple',
    icon: '🚀',
    gradient: 'from-purple-600 to-pink-600',
    careerCategories: ['business', 'tech', 'creative'],
    suggestedCareers: ['Tech Startup', 'E-Commerce', 'Content Creator', 'Freelance', 'Consulting', 'Agency Owner']
  },
  workforce: {
    id: 'workforce',
    name: 'Direct to Workforce',
    description: 'Focus on job-ready skills and entering the workforce',
    color: 'rose',
    icon: '💼',
    gradient: 'from-rose-600 to-red-600',
    careerCategories: ['business', 'creative', 'healthcare', 'trade'],
    suggestedCareers: ['Sales', 'Customer Service', 'Administrative', 'Retail Management', 'Real Estate', 'Insurance']
  },
  nc_district: {
    id: 'nc_district',
    name: 'NC District Student',
    description: 'Streamlined view aligned to North Carolina K-12 career requirements',
    color: 'cyan',
    icon: '🏫',
    gradient: 'from-cyan-600 to-blue-600',
    lockable: true,
    careerCategories: ['tech', 'healthcare', 'business', 'professional', 'creative', 'trade', 'military', 'transportation'],
    suggestedCareers: []
  }
};

// Career field mappings to focus paths
export const CAREER_TO_PATH_MAPPING = {
  // College-focused careers
  'software_engineer': ['college', 'entrepreneurship'],
  'data_scientist': ['college'],
  'cybersecurity': ['college', 'military'],
  'web_developer': ['college', 'entrepreneurship', 'workforce'],
  'it_support': ['workforce', 'trade'],
  'nurse': ['college', 'military'],
  'medical_assistant': ['workforce', 'trade'],
  'dental_hygienist': ['college'],
  'doctor': ['college'],
  'accountant': ['college'],
  'marketing_manager': ['college', 'entrepreneurship'],
  'lawyer': ['college'],
  'teacher': ['college'],
  // Trade-focused careers
  'electrician': ['trade'],
  'plumber': ['trade'],
  'hvac_tech': ['trade'],
  'welder': ['trade'],
  'mechanic': ['trade'],
  'carpenter': ['trade'],
  'diesel_tech': ['trade'],
  'cnc_machinist': ['trade'],
  'lineworker': ['trade'],
  'wind_tech': ['trade'],
  'solar_installer': ['trade'],
  // Workforce/Other
  'real_estate': ['workforce', 'entrepreneurship'],
  'insurance_agent': ['workforce'],
  'chef': ['trade', 'entrepreneurship'],
  'cosmetologist': ['trade'],
  'personal_trainer': ['workforce', 'entrepreneurship'],
  'truck_driver': ['trade', 'transportation'],
  'entrepreneur': ['entrepreneurship']
};

// Navigation items to show/hide based on focus path
const NAV_FOCUS_CONFIG = {
  college: {
    show: ['/app/dashboard', '/app/goals', '/app/plan', '/app/intelligence', '/app/quiz', 
           '/app/colleges', '/app/college-intel', '/app/scholarships', '/app/exam-dates', '/app/calculator', 
           '/app/vault', '/app/report', '/app/profile', '/app/programs', '/app/learn',
           '/app/careers', '/app/future-careers'],
    hide: ['/app/paths/military', '/app/paths/trades', '/app/paths/workforce', '/app/paths/entrepreneurship', '/app/jobs'],
    highlight: ['/app/colleges', '/app/college-intel', '/app/scholarships', '/app/exam-dates'],
    hideGroups: []
  },
  trade: {
    show: ['/app/dashboard', '/app/goals', '/app/plan', '/app/intelligence', '/app/quiz',
           '/app/paths/trades', '/app/jobs', '/app/calculator', '/app/vault', '/app/report', 
           '/app/profile', '/app/careers', '/app/future-careers', '/app/learn'],
    hide: ['/app/colleges', '/app/college-intel', '/app/scholarships', '/app/paths/military', '/app/paths/entrepreneurship', '/app/exam-dates', '/app/programs'],
    highlight: ['/app/paths/trades', '/app/jobs', '/app/careers'],
    hideGroups: ['college']
  },
  military: {
    show: ['/app/dashboard', '/app/goals', '/app/plan', '/app/intelligence', '/app/quiz',
           '/app/paths/military', '/app/calculator', '/app/vault', '/app/report', '/app/profile',
           '/app/careers', '/app/future-careers', '/app/learn'],
    hide: ['/app/colleges', '/app/college-intel', '/app/scholarships', '/app/paths/trades', '/app/paths/entrepreneurship', 
           '/app/paths/workforce', '/app/jobs', '/app/exam-dates', '/app/programs'],
    highlight: ['/app/paths/military'],
    hideGroups: ['college']
  },
  entrepreneurship: {
    show: ['/app/dashboard', '/app/goals', '/app/plan', '/app/intelligence', '/app/quiz',
           '/app/paths/entrepreneurship', '/app/calculator', '/app/vault', '/app/report', 
           '/app/profile', '/app/learn', '/app/careers', '/app/future-careers'],
    hide: ['/app/colleges', '/app/college-intel', '/app/scholarships', '/app/paths/military', '/app/paths/trades', 
           '/app/paths/workforce', '/app/exam-dates', '/app/programs'],
    highlight: ['/app/paths/entrepreneurship', '/app/learn'],
    hideGroups: ['college']
  },
  workforce: {
    show: ['/app/dashboard', '/app/goals', '/app/plan', '/app/intelligence', '/app/quiz',
           '/app/paths/workforce', '/app/jobs', '/app/calculator', '/app/vault', '/app/report', 
           '/app/profile', '/app/careers', '/app/future-careers', '/app/learn'],
    hide: ['/app/colleges', '/app/college-intel', '/app/scholarships', '/app/paths/military', '/app/paths/trades', 
           '/app/paths/entrepreneurship', '/app/exam-dates', '/app/programs'],
    highlight: ['/app/paths/workforce', '/app/jobs', '/app/careers'],
    hideGroups: ['college']
  },
  // NC District Student — locked, only shows NC Career Plan tools + core exploration
  nc_district: {
    show: [
      '/app/dashboard', '/app/goals',
      '/app/assessment', '/app/careers',
      '/app/nc-cdp', '/app/nc-cte', '/app/nc-wbl',
      '/app/certificates', '/app/credentials', '/app/course-planner',
      '/app/graduation-readiness', '/app/rec-letters', '/app/events',
      '/app/my-futurepath', '/app/profile',
    ],
    hide: [
      '/app/colleges', '/app/college-intel', '/app/scholarships', '/app/exam-dates',
      '/app/paths/military', '/app/paths/trades', '/app/paths/entrepreneurship', '/app/paths/workforce',
      '/app/jobs', '/app/programs', '/app/learn', '/app/skills-assessment',
      '/app/future-of-work', '/app/learning-roi', '/app/future-self',
      '/app/resume-ats', '/app/pathway', '/app/billing', '/app/welcome',
      '/app/pathways/compare', '/app/future-outlook', '/app/calculator',
    ],
    highlight: [
      '/app/nc-cdp', '/app/graduation-readiness', '/app/credentials',
    ],
    hideGroups: ['college', 'life', 'decide', 'report', 'account'],
    locked: true,
  }
};

export const FocusModeProvider = ({ children }) => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [focusModeEnabled, setFocusModeEnabled] = useState(false);
  const [focusedPathType, setFocusedPathType] = useState(null);
  const [focusedPathName, setFocusedPathName] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sync with user data on mount or user change
  useEffect(() => {
    if (user) {
      // Auto-detect district student from localStorage flag (set by demo portal)
      const isDistrictStudent = localStorage.getItem('institutional_shell') === '1';
      if (isDistrictStudent && user.role === 'student') {
        setFocusModeEnabled(true);
        setFocusedPathType('nc_district');
        setFocusedPathName('NC District Student');
        return;
      }
      setFocusModeEnabled(user.focus_mode_enabled || false);
      setFocusedPathType(user.focused_path_type || null);
      setFocusedPathName(user.focused_path_name || null);
    }
  }, [user]);

  // Is the current focus mode locked (cannot be changed by student)?
  const isFocusModeLocked = focusedPathType === 'nc_district' &&
    localStorage.getItem('institutional_shell') === '1';

  const enableFocusMode = useCallback(async (pathType, pathName = null) => {
    setLoading(true);
    try {
      const displayName = pathName || PATH_TYPES[pathType]?.name || pathType;
      
      const response = await axios.post(`${API}/focus-mode/enable`, {
        focus_mode_enabled: true,
        focused_path_type: pathType,
        focused_path_name: displayName
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });

      if (response.data.success) {
        setFocusModeEnabled(true);
        setFocusedPathType(pathType);
        setFocusedPathName(displayName);
        await refreshUser();
        return true;
      }
    } catch (error) {
      console.error('Failed to enable focus mode:', error);
    } finally {
      setLoading(false);
    }
    return false;
  }, [getAuthHeader, refreshUser]);

  const disableFocusMode = useCallback(async () => {
    // Locked modes cannot be disabled by the student
    if (isFocusModeLocked) return false;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/focus-mode/disable`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });

      if (response.data.success) {
        setFocusModeEnabled(false);
        setFocusedPathType(null);
        setFocusedPathName(null);
        await refreshUser();
        return true;
      }
    } catch (error) {
      console.error('Failed to disable focus mode:', error);
    } finally {
      setLoading(false);
    }
    return false;
  }, [getAuthHeader, refreshUser]);

  // Check if a navigation path should be visible
  const isNavItemVisible = useCallback((path) => {
    if (!focusModeEnabled || !focusedPathType) return true;
    
    const config = NAV_FOCUS_CONFIG[focusedPathType];
    if (!config) return true;
    
    // If in hide list, don't show
    if (config.hide.some(hidePath => path.startsWith(hidePath))) {
      return false;
    }
    
    return true;
  }, [focusModeEnabled, focusedPathType]);

  // Check if a navigation item should be highlighted
  const isNavItemHighlighted = useCallback((path) => {
    if (!focusModeEnabled || !focusedPathType) return false;
    
    const config = NAV_FOCUS_CONFIG[focusedPathType];
    if (!config) return false;
    
    return config.highlight.some(highlightPath => path.startsWith(highlightPath));
  }, [focusModeEnabled, focusedPathType]);

  // Get current path configuration
  const getCurrentPathConfig = useCallback(() => {
    if (!focusedPathType) return null;
    return PATH_TYPES[focusedPathType] || null;
  }, [focusedPathType]);

  // Check if a navigation group should be hidden
  const isNavGroupHidden = useCallback((groupId) => {
    if (!focusModeEnabled || !focusedPathType) return false;
    
    const config = NAV_FOCUS_CONFIG[focusedPathType];
    if (!config) return false;
    
    return config.hideGroups?.includes(groupId) || false;
  }, [focusModeEnabled, focusedPathType]);

  // Get suggested careers for current focus path
  const getSuggestedCareers = useCallback(() => {
    if (!focusModeEnabled || !focusedPathType) return null;
    const pathConfig = PATH_TYPES[focusedPathType];
    return pathConfig?.suggestedCareers || null;
  }, [focusModeEnabled, focusedPathType]);

  // Get allowed career categories for current focus path
  const getAllowedCareerCategories = useCallback(() => {
    if (!focusModeEnabled || !focusedPathType) return null;
    const pathConfig = PATH_TYPES[focusedPathType];
    return pathConfig?.careerCategories || null;
  }, [focusModeEnabled, focusedPathType]);

  // Check if a career matches the current focus
  const isCareerRelevant = useCallback((careerKey, careerCategory) => {
    if (!focusModeEnabled || !focusedPathType) return true;
    
    // Check direct mapping
    const pathsForCareer = CAREER_TO_PATH_MAPPING[careerKey];
    if (pathsForCareer && pathsForCareer.includes(focusedPathType)) {
      return true;
    }
    
    // Check category
    const pathConfig = PATH_TYPES[focusedPathType];
    if (pathConfig?.careerCategories?.includes(careerCategory)) {
      return true;
    }
    
    return false;
  }, [focusModeEnabled, focusedPathType]);

  const value = {
    focusModeEnabled,
    focusedPathType,
    focusedPathName,
    isFocusModeLocked,
    loading,
    enableFocusMode,
    disableFocusMode,
    isNavItemVisible,
    isNavItemHighlighted,
    isNavGroupHidden,
    getCurrentPathConfig,
    getSuggestedCareers,
    getAllowedCareerCategories,
    isCareerRelevant,
    PATH_TYPES,
    CAREER_TO_PATH_MAPPING,
    NAV_FOCUS_CONFIG
  };

  return (
    <FocusModeContext.Provider value={value}>
      {children}
    </FocusModeContext.Provider>
  );
};

export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }
  return context;
};

export default FocusModeContext;
